import type { EventHandler, KeyboardEventHandler } from "react";
import { Trans, useTranslation } from "react-i18next";

import { FloatingLabelInput, TextField } from "@aviary";
import { e2eAttributes } from "@shared/e2eAttributes";
import type {
  GetSuggestionItemProps,
  Suggestion,
} from "@shared/hooks/useAddressAutocomplete/useAddressAutocomplete";
import { l } from "@shared/locales/i18n";

import * as styles from "./AddressPulldownView.styles";

interface Props {
  suggestions: readonly Suggestion[];
  onBlur: (...args: any[]) => void | Promise<void>;
  onChange: EventHandler<any>;
  onKeyDown: KeyboardEventHandler;
  getSuggestionItemProps: GetSuggestionItemProps;
  value: string;
  name: string;
  loading: boolean;
  maxLength?: number;
  id: string;
  label: string;
  errors?: string[];
  disabled?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  icon?: any;
  "aria-label"?: string;
  // Add new props for TextField support
  useTextField?: boolean;
  hasClearButton?: boolean;
  onClearField?: () => void;
}

const AddressPulldownView = ({
  suggestions,
  onBlur,
  onChange,
  onKeyDown,
  value,
  name,
  getSuggestionItemProps,
  loading,
  maxLength,
  hideLabel,
  placeholder,
  icon,
  label,
  "aria-label": ariaLabel,
  useTextField = false,
  hasClearButton = false,
  onClearField,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const isSuggesting = suggestions.length > 0;

  const conditionalStyles = (suggesting: boolean) => {
    return [
      styles.autoCompleteDropdown.base,
      suggesting && styles.autoCompleteDropdown.state.isSuggesting,
    ];
  };

  const renderInputField = () => {
    if (useTextField) {
      return (
        <TextField
          type="text"
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          autoComplete="off"
          maxLength={maxLength}
          inputIcon={icon}
          hasClearButton={hasClearButton}
          onClearField={onClearField}
          isFullWidth
          {...rest}
        />
      );
    }

    return (
      <FloatingLabelInput
        type="text"
        name={name}
        label={label}
        required
        value={value}
        handleChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoComplete="none"
        maxLength={maxLength}
        hideLabel={hideLabel}
        placeholder={placeholder}
        icon={icon}
        {...rest}
        aria-label={ariaLabel}
      />
    );
  };

  return (
    <div css={styles.autoCompleteWrapper}>
      <div css={styles.autoCompleteFormField}>
        <div>{renderInputField()}</div>
      </div>
      <div css={conditionalStyles(isSuggesting)}>
        {!!loading && <div css={styles.autoCompleteSuggestion}>Loading...</div>}
        <div css={styles.autoCompleteHeader}>
          <span>{t(l.sharedCommon.Suggestions)}</span>
        </div>
        <AddressSuggestions
          suggestions={suggestions}
          getSuggestionItemProps={getSuggestionItemProps}
        />
        <div css={styles.autoCompleteMessage}>
          <span>
            <Trans i18nKey={l.sharedCommon.poweredByGoogle}>
              powered by <strong>Google</strong>
            </Trans>
          </span>
        </div>
      </div>
    </div>
  );
};

const AddressSuggestions = ({
  suggestions,
  getSuggestionItemProps,
}: {
  suggestions: readonly Suggestion[];
  getSuggestionItemProps: GetSuggestionItemProps;
}) => (
  <div data-e2e={e2eAttributes.paymentSettings.streetAddressSuggestionContainer}>
    {suggestions.map(suggestion => {
      const className = suggestion.active ? "active" : "";
      const { key: unused, ...rest } = getSuggestionItemProps(suggestion, {
        className,
      });

      return (
        <div
          css={styles.autoCompleteSuggestion}
          key={suggestion.description}
          data-e2e={e2eAttributes.paymentSettings.streetAddressSuggestion}
          {...rest}
        >
          <span>{suggestion.description}</span>
        </div>
      );
    })}
  </div>
);

export { AddressPulldownView };
